import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../contexts/LanguageContext';

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register, loading, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  type TranslationType = {
    title: string;
    subtitle: string;
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
    name: string;
    register: string;
    registering: string;
    alreadyHaveAccount: string;
    signIn: string;
    success: string;
    usernameRequired: string;
    emailRequired: string;
    emailInvalid: string;
    passwordRequired: string;
    passwordMinLength: string;
    passwordsDoNotMatch: string;
    registerError: string;
    usernameExists: string;
  };

  const enTranslations: TranslationType = {
    title: 'Create Account',
    subtitle: 'Sign up to get started',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    name: 'Full Name (Optional)',
    register: 'Sign Up',
    registering: 'Creating Account...',
    alreadyHaveAccount: 'Already have an account?',
    signIn: 'Sign In',
    success: 'Account created. Redirecting…',
    usernameRequired: 'Username is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    passwordRequired: 'Password is required',
    passwordMinLength: 'Password must be at least 6 characters',
    passwordsDoNotMatch: 'Passwords do not match',
    registerError: 'Failed to create account. Please try again.',
    usernameExists: 'Username already exists',
  };

  const trTranslations: TranslationType = {
    title: 'Hesap Oluştur',
    subtitle: 'Başlamak için kayıt ol',
    username: 'Kullanıcı Adı',
    email: 'E-posta',
    password: 'Şifre',
    confirmPassword: 'Şifre Tekrar',
    name: 'Ad Soyad (Opsiyonel)',
    register: 'Kayıt Ol',
    registering: 'Hesap oluşturuluyor…',
    alreadyHaveAccount: 'Zaten hesabın var mı?',
    signIn: 'Giriş Yap',
    success: 'Hesap oluşturuldu. Yönlendiriliyorsun…',
    usernameRequired: 'Kullanıcı adı gerekli',
    emailRequired: 'E-posta gerekli',
    emailInvalid: 'Geçerli bir e-posta gir',
    passwordRequired: 'Şifre gerekli',
    passwordMinLength: 'Şifre en az 6 karakter olmalı',
    passwordsDoNotMatch: 'Şifreler eşleşmiyor',
    registerError: 'Hesap oluşturulamadı. Tekrar deneyin.',
    usernameExists: 'Bu kullanıcı adı kullanılıyor',
  };

  const translations: Record<string, TranslationType> = {
    en: enTranslations,
    tr: trTranslations,
    fr: enTranslations,
    es: enTranslations,
    ar: enTranslations,
    de: enTranslations,
    it: enTranslations,
    ru: enTranslations,
  };

  const trans: TranslationType = translations[language] || enTranslations;

  const validateForm = (): boolean => {
    if (!formData.username.trim()) { setError(trans.usernameRequired); return false; }
    if (!formData.email.trim()) { setError(trans.emailRequired); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setError(trans.emailInvalid); return false; }
    if (!formData.password) { setError(trans.passwordRequired); return false; }
    if (formData.password.length < 6) { setError(trans.passwordMinLength); return false; }
    if (formData.password !== formData.confirmPassword) { setError(trans.passwordsDoNotMatch); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!validateForm()) return;

    try {
      await register(
        formData.username.trim(),
        formData.email.trim(),
        formData.password,
        formData.name.trim() || undefined
      );
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: any) {
      let errorMessage = trans.registerError;
      if (err?.response?.status === 400) {
        if (err?.response?.data?.detail?.includes('already exists') || err?.response?.data?.detail?.includes('Username')) {
          errorMessage = trans.usernameExists;
        } else {
          errorMessage = err?.response?.data?.detail || errorMessage;
        }
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-background flex relative overflow-hidden">
      {/* Editorial side panel */}
      <aside className="hidden lg:flex relative w-[44%] min-h-screen border-r border-hairline flex-col justify-between p-12 xl:p-16">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            aria-label="Mr.Mirror"
          >
            <img
              src="/mr-mirror-logo.png"
              alt=""
              className="w-9 h-9 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-serif italic text-2xl text-ink leading-none">
              Mr.Mirror
              <sup className="font-mono not-italic text-[10px] text-accent ml-0.5 tracking-wider">®</sup>
            </span>
          </button>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-mute">
            INT. — KAYIT
          </span>
        </div>

        <div className="max-w-md">
          <span className="eyebrow mb-6 block">/ AÇILIŞ EVRESİ</span>
          <h2 className="display-h display-md mb-6">
            {language === 'tr' ? (
              <>Yeni bir <em>hikâyenin</em><br/>başlangıcı.</>
            ) : (
              <>The <em>opening</em> of<br/>a new story.</>
            )}
          </h2>
          <p className="font-prose text-[15px] text-ink-dim leading-relaxed max-w-sm">
            {language === 'tr'
              ? 'Hesabını aç, ilk senaryonu yükle. Mr.Mirror sayfanı sindirsin, ne demek istediğini söylesin.'
              : "Create your account and upload your first script. Mr.Mirror will read it and tell you what it sees."}
          </p>
        </div>

        <div className="flex items-center justify-between text-ink-mute">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">/ AUTH · 0002</span>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">v1.0</span>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex flex-col justify-center items-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/')}
            className="lg:hidden flex items-center gap-3 mb-12 hover:opacity-80 transition-opacity"
          >
            <img
              src="/mr-mirror-logo.png"
              alt=""
              className="w-9 h-9 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-serif italic text-2xl text-ink leading-none">
              Mr.Mirror
              <sup className="font-mono not-italic text-[10px] text-accent ml-0.5 tracking-wider">®</sup>
            </span>
          </button>

          <span className="eyebrow mb-4 block">/ 02 · {language === 'tr' ? 'Hesap Aç' : 'Create Account'}</span>
          <h1 className="display-h display-md mb-3">
            {language === 'tr' ? <>Birlikte <em>başlayalım.</em></> : <>Let's <em>begin.</em></>}
          </h1>
          <p className="font-prose text-[15px] text-ink-dim leading-relaxed mb-10">
            {trans.subtitle}
          </p>

          {success && (
            <div className="mb-6 p-4 border border-success/40 bg-accent-soft flex items-center gap-3">
              <CheckCircle className="text-success" sx={{ fontSize: 20 }} />
              <p className="font-prose text-[14px] text-ink leading-relaxed">{trans.success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 border border-signal-red/40 bg-signal-red/10">
              <span className="eyebrow-mute mb-1 block">/ ERROR</span>
              <p className="font-prose text-[14px] text-signal-red leading-relaxed">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <label className="field-label">{trans.username}</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder={trans.username}
                required
                autoComplete="username"
                className="input-cinematic"
              />
            </div>

            <div>
              <label className="field-label">{trans.email}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                autoComplete="email"
                className="input-cinematic"
              />
            </div>

            <div>
              <label className="field-label">{trans.password}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  className="input-cinematic pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  className="absolute right-0 bottom-3 w-10 h-10 flex items-center justify-center text-ink-mute hover:text-ink transition-colors"
                >
                  {showPassword ? <VisibilityOff style={{ width: '18px', height: '18px' }} /> : <Visibility style={{ width: '18px', height: '18px' }} />}
                </button>
              </div>
            </div>

            <div>
              <label className="field-label">{trans.confirmPassword}</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  className="input-cinematic pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  className="absolute right-0 bottom-3 w-10 h-10 flex items-center justify-center text-ink-mute hover:text-ink transition-colors"
                >
                  {showConfirmPassword ? <VisibilityOff style={{ width: '18px', height: '18px' }} /> : <Visibility style={{ width: '18px', height: '18px' }} />}
                </button>
              </div>
            </div>

            <div>
              <label className="field-label">{trans.name}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={trans.name}
                autoComplete="name"
                className="input-cinematic"
              />
            </div>

            <button
              type="submit"
              disabled={loading || success}
              className="btn-accent w-full justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? trans.registering : trans.register}
              <span aria-hidden>→</span>
            </button>
          </form>

          <p className="mt-10 text-center font-prose text-[14px] text-ink-dim">
            {trans.alreadyHaveAccount}{' '}
            <Link to="/login" className="text-accent hover:underline underline-offset-2">
              {trans.signIn}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
